import React, { useState, useEffect } from 'react';
import * as echarts from 'echarts';

interface CustomLegendProps {
  legendData: string[];
  colors: string[];
  chartInstance: echarts.ECharts | null;
}

const CustomLegend: React.FC<CustomLegendProps> = ({
  legendData,
  colors,
  chartInstance,
}) => {
  // Track selected state
  const [selectedSeries, setSelectedSeries] = useState<Record<string, boolean>>(
    legendData.reduce((acc: Record<string, boolean>, name: string) => {
      acc[name] = true;
      return acc;
    }, {})
  );

  // Update state when legend data changes
  useEffect(() => {
    setSelectedSeries(
      legendData.reduce((acc: Record<string, boolean>, name: string) => {
        // Preserve previous state if it exists
        acc[name] =
          selectedSeries[name] !== undefined ? selectedSeries[name] : true;
        return acc;
      }, {})
    );
  }, [legendData]);

  // Sync with chart's legend state when chart instance changes
  useEffect(() => {
    if (!chartInstance) return;

    // Get current selected state from chart
    const option: any = chartInstance.getOption();
    const legendSelected = option.legend?.[0]?.selected as
      | Record<string, boolean>
      | undefined;

    if (legendSelected) {
      setSelectedSeries((prevState) => ({
        ...prevState,
        ...legendSelected,
      }));
    }

    // Listen for legend selection change events from the chart
    const legendSelectChanged = (params: {
      name: string;
      selected: Record<string, boolean>;
    }) => {
      setSelectedSeries(params.selected);
    };

    chartInstance.on('legendselectchanged', legendSelectChanged);

    return () => {
      chartInstance.off('legendselectchanged', legendSelectChanged);
    };
  }, [chartInstance]);

  const toggleSeries = (name: string) => {
    if (!chartInstance) return;

    // Update local state
    const newState = {
      ...selectedSeries,
      [name]: !selectedSeries[name],
    };

    setSelectedSeries(newState);

    // Use legendToggleSelect to toggle series visibility
    chartInstance.dispatchAction({
      type: 'legendToggleSelect',
      name: name,
    });
  };

  return (
    <div
      style={{
        width: '100%',
        maxHeight: '80px',
        overflowY: 'auto',
        display: 'flex',
        flexWrap: 'wrap',
        padding: '8px 2.5%',
        // borderTop: '1px solid #eee',
      }}
    >
      {legendData.map((name: string, index: number) => (
        <div
          key={name}
          onClick={() => toggleSeries(name)}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '2px 10px',
            cursor: 'pointer',
            marginRight: '10px',
            marginBottom: '5px',
            opacity: selectedSeries[name] ? 1 : 0.5,
          }}
        >
          <div
            style={{
              width: '14px',
              height: '14px',
              backgroundColor: colors[index % colors.length],
              marginRight: '5px',
              borderRadius: '2px',
            }}
          />
          <span style={{ fontSize: '12px' }}>{name}</span>
        </div>
      ))}
    </div>
  );
};

export default CustomLegend;
